import { invoke } from "@tauri-apps/api/core";
import * as path from "@tauri-apps/api/path";
import * as taurifs from "@tauri-apps/plugin-fs";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import YAML from "yaml";

import { Logger } from "@moment/logging";

import { env } from "../env/env";
import { $gitStatus } from "../git";

const logger = new Logger("filesystem");

const GIT_STATUS_REFRESH_DEBOUNCE_MS = 2000;
let gitStatusRefreshTimestamp = 0;

// PathJoin atom family (internal use only)
const $pathJoin = atomFamily((paths: string[]) => atom(() => path.join(...paths)));

// Path dirname atom family (internal use only)
const $dirname = atomFamily((filePath: string) => atom(() => path.dirname(filePath)));

// Read Operations

const $readDir = atomFamily(
	({ dirPath, options }: { dirPath: string; options?: taurifs.ReadDirOptions }) => {
		return atom(async () => await taurifs.readDir(dirPath, options));
	}
);

const $readTextFile = atomFamily(
	({ filePath, options }: { filePath: string; options?: taurifs.ReadFileOptions }) => {
		return atom(async () => await taurifs.readTextFile(filePath, options));
	}
);

const $readBinaryFile = atomFamily(
	({ filePath, options }: { filePath: string; options?: taurifs.ReadFileOptions }) => {
		return atom(async () => await taurifs.readFile(filePath, options));
	}
);

const $readJsonFile = atomFamily(
	({ filePath, options }: { filePath: string; options?: taurifs.ReadFileOptions }) => {
		const logger = new Logger("filesystem/readJsonFile");

		return atom(async (get) => {
			const text = await get($readTextFile({ filePath, options }));
			try {
				return JSON.parse(text);
			} catch (error) {
				logger.error("JSON parse error", {
					filePath,
					options,
					text,
					cause: error,
				});
				throw new Error(
					`JSON parse error: ${error instanceof Error ? error.message : String(error)}`
				);
			}
		});
	}
);

const $readYamlFile = atomFamily(
	({ filePath, options }: { filePath: string; options?: taurifs.ReadFileOptions }) => {
		const logger = new Logger("filesystem/readYamlFile");

		return atom(async (get) => {
			const text = await get($readTextFile({ filePath, options }));

			try {
				return YAML.parse(text);
			} catch (error) {
				logger.error("YAML parse error", {
					filePath,
					options,
					text,
					cause: error,
				});
				throw new Error("YAML parse error");
			}
		});
	}
);

const $exists = ({ filePath, options }: { filePath: string; options?: taurifs.ReadFileOptions }) =>
	atom(async () => await taurifs.exists(filePath, options));

// Write Operations

const $writeTextFile = atom(
	null,
	async (_get, set, variables: { filePath: string; content: string; documentId: string }) => {
		logger.debug("Writing text file", { filePath: variables.filePath });
		logger.debug("Invoking write_text_file", { filePath: variables.filePath });

		await invoke("write_text_file", new TextEncoder().encode(variables.content), {
			headers: {
				path: encodeURIComponent(variables.filePath),
			},
		});
		logger.debug("write_text_file invocation complete", { filePath: variables.filePath });
		const ts = Date.now();
		gitStatusRefreshTimestamp = ts;
		setTimeout(() => {
			if (gitStatusRefreshTimestamp === ts) {
				set($gitStatus(variables.documentId));
			}
		}, GIT_STATUS_REFRESH_DEBOUNCE_MS);
	}
);

const $writeBinaryFile = atom(
	null,
	async (
		_get,
		_set,
		variables: {
			filePath: string;
			content: Uint8Array;
			options?: taurifs.WriteFileOptions;
		}
	) => {
		await taurifs.writeFile(variables.filePath, variables.content, variables.options);
	}
);

const $writeJsonFile = atom(
	null,
	async (_get, set, variables: { filePath: string; content: unknown; documentId: string }) => {
		await set($writeTextFile, {
			filePath: variables.filePath,
			content: JSON.stringify(variables.content, null, "\t") + "\n",
			documentId: variables.documentId,
		});
	}
);

const $writeYamlFile = atom(
	null,
	async (_get, set, variables: { filePath: string; content: unknown; documentId: string }) => {
		await set($writeTextFile, {
			filePath: variables.filePath,
			content: YAML.stringify(variables.content),
			documentId: variables.documentId,
		});
	}
);

const $rename = atom(
	null,
	async (_get, set, variables: { oldPath: string; newPath: string; documentId: string }) => {
		logger.debug("Renaming", { oldPath: variables.oldPath, newPath: variables.newPath });
		await taurifs.rename(variables.oldPath, variables.newPath);
		const ts = Date.now();
		gitStatusRefreshTimestamp = ts;
		setTimeout(() => {
			if (gitStatusRefreshTimestamp === ts) {
				set($gitStatus(variables.documentId));
			}
		}, GIT_STATUS_REFRESH_DEBOUNCE_MS);
	}
);

const $mkdir = atom(
	null,
	async (_get, _set, variables: { dirPath: string; options?: taurifs.MkdirOptions }) => {
		await taurifs.mkdir(variables.dirPath, {
			recursive: true,
			...variables.options,
		});
	}
);

const $create = atom(
	null,
	async (_get, _set, variables: { filePath: string; options?: taurifs.CreateOptions }) => {
		await taurifs.create(variables.filePath, variables.options);
	}
);

const $remove = atom(
	null,
	async (_get, _set, variables: { filePath: string; options?: taurifs.RemoveOptions }) => {
		await taurifs.remove(variables.filePath, {
			...variables.options,
			recursive: true,
		});
	}
);

const noOpFamily = atomFamily((result: any) => atom(() => Promise.resolve(result)));

const noOpQueryFamily = atomFamily((result: any) => atom(() => Promise.resolve(result)));

// No-op atom setter for when filesystem is not available
const noOpSetter = atom(null, async () => {
	// No-op when filesystem is not available
});

const tauriFilesystemAPI = {
	readDir: (params: Parameters<typeof $readDir>[0]) => $readDir(params),
	readTextFile: (params: Parameters<typeof $readTextFile>[0]) => $readTextFile(params),
	readBinaryFile: (params: Parameters<typeof $readBinaryFile>[0]) => $readBinaryFile(params),
	readJsonFile: (params: Parameters<typeof $readJsonFile>[0]) => $readJsonFile(params),
	readYamlFile: (params: Parameters<typeof $readYamlFile>[0]) => $readYamlFile(params),
	exists: (params: Parameters<typeof $exists>[0]) => $exists(params),
	pathJoin: (params: Parameters<typeof $pathJoin>[0]) => $pathJoin(params),
	dirname: (params: Parameters<typeof $dirname>[0]) => $dirname(params),
	writeTextFile: $writeTextFile,
	writeBinaryFile: $writeBinaryFile,
	writeJsonFile: $writeJsonFile,
	writeYamlFile: $writeYamlFile,
	rename: $rename,
	mkdir: $mkdir,
	create: $create,
	remove: $remove,
};

const noOpFilesystemAPI: typeof tauriFilesystemAPI = {
	readDir: () => noOpQueryFamily([]),
	readTextFile: () => noOpQueryFamily([]),
	readBinaryFile: () => noOpQueryFamily([]),
	readJsonFile: () => noOpQueryFamily([]),
	readYamlFile: () => noOpQueryFamily([]),
	exists: () => noOpQueryFamily([]),
	pathJoin: () => noOpFamily([]),
	dirname: () => noOpFamily(""),
	writeTextFile: noOpSetter,
	writeBinaryFile: noOpSetter,
	writeJsonFile: noOpSetter,
	writeYamlFile: noOpSetter,
	rename: noOpSetter,
	mkdir: noOpSetter,
	create: noOpSetter,
	remove: noOpSetter,
};

// Unified filesystem API
export const fs = () => {
	const api: typeof tauriFilesystemAPI = env.isFilesystemAvailable
		? tauriFilesystemAPI
		: noOpFilesystemAPI;

	return api;
};
